import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { api } from "./api";
import { refreshMediaTicket, stopMediaTicket } from "./mediaTicket";

const AuthContext = createContext(null);

const REFRESH_INTERVAL_MS = 45 * 60 * 1000; // silent refresh every 45 min
const FOCUS_THROTTLE_MS = 5 * 60 * 1000; // at most one focus-triggered refresh per 5 min

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("kairos_token"));
  const [sessionExpired, setSessionExpired] = useState(false);
  const lastRefresh = useRef(Date.now());

  const save = useCallback((tok) => {
    localStorage.setItem("kairos_token", tok);
    setToken(tok);
  }, []);

  // Passwordless email-OTP login.
  const requestOtp = useCallback(async (email) => {
    const { data } = await api.post("/auth/request-otp", { email });
    return data; // { sent, email, email_delivered, dev_otp? }
  }, []);

  // Verify only — returns data WITHOUT saving the token, so the UI can play a
  // success animation before completing login.
  const verifyOtp = useCallback(async (email, code) => {
    const { data } = await api.post("/auth/verify-otp", { email, code });
    return data; // { access_token, role }
  }, []);

  const completeLogin = useCallback((tok) => {
    setSessionExpired(false);
    lastRefresh.current = Date.now();
    save(tok);
    refreshMediaTicket();
  }, [save]);

  const logout = useCallback(() => {
    localStorage.removeItem("kairos_token");
    setToken(null);
    setSessionExpired(false);
    stopMediaTicket();
  }, []);

  // Clean logout when any API call comes back 401 (token expired/invalid).
  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setSessionExpired(true);
    };
    window.addEventListener("kairos-session-expired", onExpired);
    return () => window.removeEventListener("kairos-session-expired", onExpired);
  }, []);

  // Sliding session: silently refresh the token while the tab stays open.
  useEffect(() => {
    if (!token) return;
    refreshMediaTicket(); // ensure a media ticket exists on boot / reload
    const refresh = async () => {
      try {
        const { data } = await api.post("/auth/refresh");
        if (data?.access_token) {
          lastRefresh.current = Date.now();
          save(data.access_token);
        }
      } catch {
        // 401 is handled globally via the kairos-session-expired event
      }
    };
    const id = setInterval(refresh, REFRESH_INTERVAL_MS);
    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefresh.current < FOCUS_THROTTLE_MS) return;
      refresh();
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [token, save]);

  return (
    <AuthContext.Provider value={{ token, sessionExpired, requestOtp, verifyOtp, completeLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
